import axios, { AxiosPromise, AxiosResponse, CancelToken } from 'axios';
import * as queryString from 'query-string';

import {
	AccountStatusEnum,
	BrowserStorageType,
	CarriersEnum,
	EventTypeEnum,
	TransactionTypeEnum,
	UserRole,
} from '@app/app.enum';
import environment from '@app/environments/environment';
import * as models from '@app/models/index';
import Injector from '@app/services';
import { Injectable } from 'injection-js';
import { SharedService } from './shared.service';

const accessToken = 'accessToken';
const refreshToken = 'refreshToken';
const userToken = 'UserData';
const apiKey = 'apiKey';
const visibleAccountNumber = 'visibleAccountNumber';
const status = 'status';

@Injectable()
export class AccountService {
	private sharedService: SharedService = Injector.get(SharedService);
	private baseUrl = environment.apiGateway.apiUrl;
	private userApiUrl = '';
	private accountApiUrl = '';
	private addressApiUrl = '';

	constructor() {
		this.userApiUrl = this.baseUrl + 'User/';
		this.accountApiUrl = this.baseUrl + 'Account/';
		this.addressApiUrl = this.baseUrl + 'Address/';
	}

	/**
	 * Check, if user already authorized.
	 */
	public get IsAuthorized(): boolean {
		return !!this.getAccessToken();
	}

	/**
	 * user sign in service method
	 */
	public async signIn(
		data: models.IUserSignInRequest,
		parentAccountId: string,
		partnerAppId: string,
		cancelToken?: CancelToken
	): Promise<models.IUserSignInResponse> {
		const url = this.userApiUrl + 'SignIn/';
		const res = await axios.post<models.IUserSignInResponse>(url, data, {
			cancelToken,
			params: {
				parentAccountId,
				partnerAppId,
			},
		});
		return res.data;
	}

	/**
	 * User sign up service method - Partner
	 */
	public async signUpPartner(
		data: models.IUserSignUpRequest,
		cancelToken?: CancelToken
	): Promise<models.IUserSignInResponse> {
		try {
			const url = this.userApiUrl + 'SignUp/Partner';
			const res = await axios.post<models.IUserSignInResponse>(url, data, { cancelToken });
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * User sign up service method - Customer
	 */
	public async signUpCustomer(
		data: models.IUserSignUpRequest,
		parentAccountId: string,
		partnerAppId: string,
		cancelToken?: CancelToken
	): Promise<models.IUserSignInResponse> {
		try {
			const url = this.userApiUrl + 'SignUp/Customer';
			const res = await axios.post<models.IUserSignInResponse>(url, data, {
				cancelToken,
				params: {
					parentAccountId,
					partnerAppId,
				},
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Approve terms and condition
	 */
	public async approveTermsAndConditions(
		data: models.IApproveTermsAndConditions
	): Promise<models.IApproveTermsAndConditions> {
		try {
			const url = this.userApiUrl + 'ApproveTermsAndConditions';
			const res = await axios.post<models.IApproveTermsAndConditions>(url, data);
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * User sign up service method - PackageHubCustomer
	 */
	public async signUpPackageHubCustomer(cancelToken?: CancelToken): Promise<models.IAccountAppResponseVM> {
		try {
			const url = this.userApiUrl + 'SignUp/PackageHubCustomer';
			const res = await axios.post<models.IAccountAppResponseVM>(url, null, { cancelToken });
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public signOut(): void {
		// TODO: Hack it is. Do it proper way with state management
		// This is to fix an issue in which after signing out user is not able to visit signup page
		sessionStorage.removeItem(accessToken);
		sessionStorage.removeItem(refreshToken);
		sessionStorage.removeItem(userToken);
		localStorage.removeItem(accessToken);
		localStorage.removeItem(refreshToken);
		localStorage.removeItem(userToken);
		localStorage.removeItem(apiKey);
		localStorage.removeItem(status);
	}

	/**
	 * refreshToken method, called in axios response interceptor
	 * @param data
	 */
	public async refreshToken(data: models.IUserRefreshTokenRequest): Promise<models.IUserSignInResponse> {
		try {
			const url = this.userApiUrl + 'Refresh/';
			const res = await axios.post<models.IUserSignInResponse>(url, data);
			return res.data;
		} catch (error) {
			throw error;
		}
	}
	/**
	 *  get user own record
	 */

	public async getOwnUserRecord(cancelToken?: CancelToken): Promise<models.IUser> {
		try {
			const url = this.userApiUrl + 'Me/';
			const res = await axios.get<models.IUser>(url, { cancelToken });
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * user Forgot password request
	 */
	public async userForgotPassword(
		data: models.IUserForgotPasswordRequest,
		cancelToken?: CancelToken
	): Promise<boolean> {
		try {
			const url = this.userApiUrl + 'ForgotPassword/';
			const res = await axios.post<boolean>(url, data, { cancelToken });
			return res.data;
		} catch (error) {
			throw error;
		}
	}
	/**
	 * user Reset password request
	 */
	public async userResetPassword(data: models.IResetPasswordRequest, cancelToken?: CancelToken): Promise<boolean> {
		try {
			const url = this.userApiUrl + 'ResetPassword';
			const res = await axios.put<boolean>(url, data, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Get access token from storage
	 */
	public getAccessToken(): string {
		return sessionStorage.getItem(accessToken) || localStorage.getItem(accessToken);
	}

	/**
	 * Get refresh token from storage
	 */
	public getRefreshToken(): string {
		return sessionStorage.getItem(refreshToken) || localStorage.getItem(refreshToken);
	}

	/**
	 * Modifies access token in storage
	 */
	public modifyAccessToken(token: string): void {
		let oldToken = sessionStorage.getItem(accessToken);
		let browserStorageType = BrowserStorageType.Session;
		if (!oldToken) {
			oldToken = localStorage.getItem(accessToken);
			browserStorageType = BrowserStorageType.Local;
		}
		this.setAccessToken(token, browserStorageType);
	}

	/**
	 * Modifies refresh token in storage
	 */
	public modifyRefreshToken(token: string): void {
		let oldToken = sessionStorage.getItem(refreshToken);
		let browserStorageType = BrowserStorageType.Session;
		if (!oldToken) {
			oldToken = localStorage.getItem(refreshToken);
			browserStorageType = BrowserStorageType.Local;
		}
		this.setRefreshToken(token, browserStorageType);
	}

	/**
	 * Set access token
	 */
	public setAccessToken(token: string, browserStorageType: BrowserStorageType) {
		switch (browserStorageType) {
			case BrowserStorageType.Local:
				return localStorage.setItem(accessToken, token);

			case BrowserStorageType.Session:
				return sessionStorage.setItem(accessToken, token);
		}
	}

	/**
	 * Set refresh token
	 */
	public setRefreshToken(token: string, browserStorageType: BrowserStorageType) {
		switch (browserStorageType) {
			case BrowserStorageType.Local:
				return localStorage.setItem(refreshToken, token);

			case BrowserStorageType.Session:
				return sessionStorage.setItem(refreshToken, token);
		}
	}

	public setUserData(userData: models.IUserSignInResponse) {
		localStorage.setItem('UserData', JSON.stringify(userData));
	}

	/**
	 * Get user data from storage
	 */
	public getUserData(): models.IUserSignInResponse {
		return JSON.parse(localStorage.getItem('UserData'));
	}

	/**
	 *  get Account details
	 */
	public async getAccountDetails(cancelToken?: CancelToken, authHeader?: string): Promise<models.IAccount> {
		try {
			const url = this.accountApiUrl + 'Postage/Balance';
			const res = await axios.get<models.IAccount>(url, {
				cancelToken,
				headers: {
					['Authorization']: `Bearer ${authHeader}`,
				},
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 *  get Profile Completeness detail
	 */
	public async getProfileCompletenessInfo(cancelToken?: CancelToken): Promise<models.IProfileCompleteness> {
		try {
			const url = this.accountApiUrl + 'ProfileCompleteness/';
			const res = await axios.get<models.IProfileCompleteness>(url, { cancelToken });
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 *  update account details
	 */

	public async updateAccountAutoBuyDetail(
		data: models.IAutoBuyFields,
		cancelToken?: CancelToken
	): Promise<models.IAccount> {
		try {
			const url = this.accountApiUrl + 'Postage/AutoBuy';
			const res = await axios.put<models.IAccount>(url, data, {
				cancelToken,
				headers: {
					TransactionId: this.sharedService.createTransactionId(),
				},
			});

			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public async updateAccountBalance(data: models.IBillingBalance, cancelToken?: CancelToken): Promise<models.IAccount> {
		try {
			const url = this.accountApiUrl + 'Postage/Balance';
			const res = await axios.put<models.IAccount>(url, data, {
				cancelToken,
				headers: {
					TransactionId: this.sharedService.createTransactionId(),
				},
			});

			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Update payment method
	 */
	public async updatePaymentMethodDetails(
		data: models.IPaymentMethod,
		cancelToken?: CancelToken,
		reqAuthHeader?: string
	): Promise<models.IPaymentMethod> {
		try {
			const url = this.accountApiUrl + 'Payment';
			const res = await axios.put<models.IPaymentMethod>(url, data, {
				cancelToken,
				headers: {
					['Authorization']: `Bearer ${reqAuthHeader}`,
				},
			});

			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public addApp(
		app: models.IAccountAppResponseVM,
		cancelToken?: CancelToken
	): AxiosPromise<models.IAccountAppResponseVM> {
		const url = this.accountApiUrl + 'App';
		return axios.post(url, app, { cancelToken });
	}

	public editApp(
		app: models.IAccountAppResponseVM,
		cancelToken?: CancelToken
	): AxiosPromise<models.IAccountAppResponseVM> {
		const url = this.accountApiUrl + 'App';
		return axios.put(url, app, { cancelToken });
	}

	public async DeleteApp(appId: string, cancelToken?: CancelToken): Promise<boolean> {
		try {
			const url = this.accountApiUrl + `App/${appId}`;
			const res = await axios.delete(url, { cancelToken });
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public IsAdmin(role?: UserRole): boolean {
		if (!role) {
			role = this.getUserData().user.role;
		}
		if (role === UserRole.Admin || role === UserRole.ReadOnlyAdmin) {
			return true;
		}
		return false;
	}

	public async regenerateApiCreds(
		apiKeyVal: string,
		authHeaderVal?: string
	): Promise<models.IAppRegenerateCredentials> {
		const url = this.accountApiUrl + 'RegenerateAppCreds';

		try {
			const res = await axios.post(
				url,
				{ apiKey: apiKeyVal },
				{
					headers: {
						['Authorization']: `Bearer ${authHeaderVal}`,
					},
				}
			);

			return res.data;
		} catch (err) {
			throw err;
		}
	}

	// update user basic details
	public async updateUserBasicInfo(userData: models.IUserBasicInfo, cancelToken?: CancelToken): Promise<models.IUser> {
		try {
			const url = this.userApiUrl + 'Profile';
			const res = await axios.put<models.IUser>(url, userData, {
				cancelToken,
			});

			return res.data;
		} catch (error) {
			throw error;
		}
	}

	// update carrier config address
	public async createCarrierConfigAddress(
		senderData: models.ISenderInfoFields,
		carrierCode: CarriersEnum,
		accountId: string,
		cancelToken?: CancelToken
	): Promise<models.IAddress> {
		try {
			const res = await axios.post<models.IAddress>(this.addressApiUrl, senderData, {
				cancelToken,
				params: {
					carrierCode,
					accountId,
				},
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	// get address
	public async getAddress(id: string, cancelToken?: CancelToken): Promise<models.IAddress> {
		try {
			const url = this.addressApiUrl + id;
			const res = await axios.get<models.IAddress>(url, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	// changes user's password
	public async ChangePassword(changePasswordData: models.IChangePassword, cancelToken?: CancelToken): Promise<boolean> {
		try {
			const url = this.userApiUrl + 'Password';
			const res = await axios.put<boolean>(url, changePasswordData, {
				cancelToken,
			});

			return res.data;
		} catch (error) {
			throw error;
		}
	}

	// Resend Verification Email
	public async ResendVerificationEmail(cancelToken?: CancelToken): Promise<models.IUser> {
		try {
			const url = this.userApiUrl + 'ResendVerificationEmail?email=' + this.getUserData().user.email;
			const res = await axios.get<models.IUser>(url, { cancelToken });
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * user Reset password request
	 */
	public async SendEmailToCustomer(addCustomer: models.IAddCustomer): Promise<boolean> {
		try {
			const url = this.userApiUrl + 'SendEmailToCustomer';
			const res = await axios.post<boolean>(url, addCustomer);
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * user Reset password request
	 */
	public async UploadCompanyLogo(logo: File, cancelToken?: CancelToken): Promise<string> {
		try {
			const formData: FormData = new FormData();
			formData.append('logo', logo, logo.name);
			const url = this.accountApiUrl + 'Logo';
			const res = await axios.post<string>(url, formData, {
				cancelToken,
				headers: { 'Content-Type': 'multipart/form-data' },
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Get Account App
	 */
	public async GetInvitationData(
		id: string,
		accountId: string,
		cancelToken?: CancelToken
	): Promise<models.IAccountInvitationResponseVM> {
		try {
			const res = await axios.get<models.IAccountInvitationResponseVM>(
				this.accountApiUrl + accountId + '/App/' + id + '/Invitation',
				{ cancelToken }
			);
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Get Partner invitation data
	 */
	public async GetPartnerInvitationData(
		accountId: string,
		cancelToken?: CancelToken
	): Promise<models.IAccountInvitationResponseVM> {
		try {
			const res = await axios.get<models.IAccountInvitationResponseVM>(this.accountApiUrl + accountId + '/Invitation', {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 *  get Account balance history details
	 */
	public async getAccountBalanceHistory(
		pageToken: string,
		startDate: Date,
		endDate: Date,
		selectedEventTypeFilterValues: EventTypeEnum[] = [],
		selectedTransactionTypeFilterValues: TransactionTypeEnum[] = [],
		cancelToken?: CancelToken
	): Promise<models.IPagedResult<models.IAccountTransaction>> {
		try {
			const url = this.accountApiUrl + 'Postage/BalanceHistory';

			const res = await axios.get<models.IPagedResult<models.IAccountTransaction>>(url, {
				cancelToken,
				params: {
					pageToken,
					startDate: startDate.toISOString(),
					endDate: endDate.toISOString(),
					eventTypes: selectedEventTypeFilterValues,
					transactionTypes: selectedTransactionTypeFilterValues,
				},
				paramsSerializer: (params) => queryString.stringify(params),
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	/**
	 *  get Account balance history details
	 */
	public async exportBalanceHistoryReport(
		startDate: Date,
		endDate: Date,
		selectedEventTypeFilterValues: EventTypeEnum[],
		selectedTransactionTypeFilterValues: TransactionTypeEnum[],
		cancelToken?: CancelToken
	): Promise<AxiosResponse<Blob>> {
		try {
			const url = this.accountApiUrl + 'Postage/BalanceHistory/Export';
			const res = await axios.get<Blob>(url, {
				cancelToken,
				responseType: 'blob',
				params: {
					startDate: startDate.toISOString(),
					endDate: endDate.toISOString(),
					eventTypes: selectedEventTypeFilterValues,
					transactionTypes: selectedTransactionTypeFilterValues,
				},
				paramsSerializer: (params) => queryString.stringify(params),
			});
			return res;
		} catch (error) {
			throw error;
		}
	}

	/**
	 *
	 * @param data
	 * @param cancelToken
	 * @returns
	 */
	public async downloadReceipt(transactionId: string, cancelToken?: CancelToken): Promise<AxiosResponse<Blob>> {
		try {
			const url = this.accountApiUrl + 'Receipt';
			const res = await axios.get<Blob>(url, {
				cancelToken,
				responseType: 'blob',
				params: {
					transactionId,
				},
				paramsSerializer: (params) => queryString.stringify(params),
			});
			return res;
		} catch (error) {
			throw error;
		}
	}

	/**
	 close account request	
	*/
	public async CloseAccount(data: models.ICloseAccountRequest, cancelToken?: CancelToken): Promise<boolean> {
		try {
			const url = this.accountApiUrl + 'Close';
			const res = await axios.post<boolean>(url, data, { cancelToken });
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public async GetCarrierAccounts(
		appId: string,
		cancelToken: CancelToken,
		fetchAll?: boolean
	): Promise<models.ICarrierAccounts[]> {
		try {
			const url = this.baseUrl + 'CarrierAccount';
			const res = await axios.get<models.ICarrierAccounts[]>(url, {
				cancelToken,
				params: {
					appId,
					fetchAll,
				},
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public async CreateCarrierAccount(
		data: any,
		appId: string,
		cancelToken?: CancelToken
	): Promise<models.ICarrierAccounts> {
		try {
			const url = this.baseUrl + 'CarrierAccount';
			const res = await axios.post<models.ICarrierAccounts>(url, data, {
				cancelToken,
				params: {
					appId,
				},
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public async EditCarrierAccount(
		data: any,
		appId: string,
		cancelToken?: CancelToken
	): Promise<models.ICarrierAccounts> {
		try {
			const url = this.baseUrl + 'CarrierAccount';
			const res = await axios.put<models.ICarrierAccounts>(url, data, {
				cancelToken,
				params: {
					appId,
				},
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public async DeleteCarrierAccount(appId: string, carrierId: string, cancelToken?: CancelToken): Promise<boolean> {
		try {
			const url = this.baseUrl + `CarrierAccount/${carrierId}`;
			const res = await axios.delete(url, {
				cancelToken,
				params: {
					appId,
				},
			});
			return res.data;
		} catch (err) {
			throw err;
		}
	}

	public async getAppById(appId: string, cancelToken?: CancelToken): Promise<models.IAccountAppResponseVM> {
		try {
			const url = this.accountApiUrl + `App/${appId}`;
			const res = await axios.get<models.IAccountAppResponseVM>(url, {
				cancelToken,
			});

			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public async UpdateSequenceNumber(
		sequenceNumberVM: models.ISequenceNumberVM,
		cancelToken?: CancelToken
	): Promise<boolean> {
		try {
			const url = this.baseUrl + 'CarrierAccount/SequenceNumber';
			const res = await axios.put<boolean>(url, sequenceNumberVM, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public async UpdateFedExSequenceNumber(
		sequenceNumberVM: models.IFedExNativeUpsertSequenceNumber,
		cancelToken?: CancelToken
	): Promise<number> {
		try {
			const url = this.baseUrl + 'CarrierAccount/SequenceNumber/FedEx';
			const res = await axios.put<number>(url, sequenceNumberVM, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public async GetSequenceNumber(
		carrierAccountId: string,
		cancelToken?: CancelToken
	): Promise<models.ISequenceNumberVM> {
		try {
			const url = this.baseUrl + 'CarrierAccount/' + carrierAccountId + '/SequenceNumber';
			const res = await axios.get<models.ISequenceNumberVM>(url, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public async GetFedExSequenceNumber(
		request: models.IGetFedExSequenceNumberRequestVM,
		cancelToken?: CancelToken
	): Promise<models.IFedExSequenceNumberVM> {
		try {
			const url = this.baseUrl + `CarrierAccount/SequenceNumber/FedEx`;
			const res = await axios.post<models.IFedExSequenceNumberVM>(url, request, {
				cancelToken,
			});
			return res.data;
		} catch (error) {
			throw error;
		}
	}

	public get VisibleAccountNumber(): string {
		return localStorage.getItem(visibleAccountNumber);
	}

	public set VisibleAccountNumber(visibleAccountNumberVal: string) {
		localStorage.setItem(visibleAccountNumber, visibleAccountNumberVal);
	}

	public get AccountStatus(): AccountStatusEnum {
		return localStorage.getItem(status) as AccountStatusEnum;
	}

	public set AccountStatus(statusID: AccountStatusEnum) {
		localStorage.setItem(status, statusID.toString());
	}
}



// WEBPACK FOOTER //
// ./src/services/account.service.ts